import { createSlice } from '@reduxjs/toolkit';

import { fetchNotifications, markNotificationAsRead } from './notifications-api';
import type { NotificationItem, NotificationStatus } from '@/types/notification';
import { DEFAULT_ROWS_PER_PAGE } from '@/constant';
import { logout } from '../auth/logout';

export { fetchNotifications, markNotificationAsRead } from './notifications-api';
export type { FetchNotificationsParams, MarkNotificationAsReadParams } from './notifications-api';

export interface NotificationsState {
    items: NotificationItem[];
    total: number;
    page: number;
    limit: number;
    statusFilter?: NotificationStatus;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
    currentRequestId: string | null;
    loadMoreRequestId: string | null;
    markingAsReadId: string | null;
    markAsReadError: string | null;
}

const initialState: NotificationsState = {
    items: [],
    total: 0,
    page: 1,
    limit: DEFAULT_ROWS_PER_PAGE,
    statusFilter: undefined,
    loading: false,
    loadingMore: false,
    error: null,
    currentRequestId: null,
    loadMoreRequestId: null,
    markingAsReadId: null,
    markAsReadError: null
};

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        clearNotificationsState: () => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNotifications.pending, (state, action) => {
                state.error = null;
                if (action.meta.arg?.append) {
                    state.loadingMore = true;
                    state.loadMoreRequestId = action.meta.requestId;
                    return;
                }
                state.loading = true;
                state.currentRequestId = action.meta.requestId;
            })
            .addCase(fetchNotifications.fulfilled, (state, { meta, payload }) => {
                const isAppend = meta.arg?.append;

                if (isAppend) {
                    if (state.loadMoreRequestId !== meta.requestId) {
                        return;
                    }
                    state.loadingMore = false;
                    state.loadMoreRequestId = null;
                } else {
                    if (state.currentRequestId !== meta.requestId) {
                        return;
                    }
                    state.loading = false;
                    state.currentRequestId = null;
                }

                if (isAppend) {
                    const existingIds = new Set(state.items.map((item) => item.id));
                    const newItems = payload.items.filter((item) => !existingIds.has(item.id));
                    state.items = [...state.items, ...newItems];
                } else {
                    state.items = payload.items;
                }

                state.total = payload.total;
                state.page = payload.page;
                state.limit = payload.limit;
                state.statusFilter = payload.status;
                state.error = null;
            })
            .addCase(fetchNotifications.rejected, (state, { meta, payload, error }) => {
                const isAppend = meta.arg?.append;

                if (isAppend) {
                    if (state.loadMoreRequestId !== meta.requestId) {
                        return;
                    }
                    state.loadingMore = false;
                    state.loadMoreRequestId = null;
                } else {
                    if (state.currentRequestId !== meta.requestId) {
                        return;
                    }
                    state.loading = false;
                    state.currentRequestId = null;
                }

                if (error.name === 'AbortError') {
                    state.error = null;
                } else {
                    state.error = payload ?? 'Failed to fetch notifications';
                }
            })
            .addCase(markNotificationAsRead.pending, (state, action) => {
                state.markingAsReadId = action.meta.arg.id;
                state.markAsReadError = null;
            })
            .addCase(markNotificationAsRead.fulfilled, (state, { payload }) => {
                state.markingAsReadId = null;
                state.markAsReadError = null;

                if (state.statusFilter === 'unread') {
                    const hadItem = state.items.some((item) => item.id === payload.id);
                    state.items = state.items.filter((item) => item.id !== payload.id);
                    if (hadItem && state.total > 0) {
                        state.total -= 1;
                    }
                    return;
                }

                const index = state.items.findIndex((item) => item.id === payload.id);
                if (index !== -1) {
                    state.items[index] = payload;
                }
            })
            .addCase(markNotificationAsRead.rejected, (state, { payload }) => {
                state.markingAsReadId = null;
                state.markAsReadError = payload ?? 'Failed to mark notification as read';
            })
            .addCase(logout, () => initialState);
    }
});

export const { clearNotificationsState } = notificationsSlice.actions;
export default notificationsSlice.reducer;
